import { useEffect, useState, useRef } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { supabaseClient } from '../config/supabase'
import { useAuthStore } from '../store/authStore'
import { Calendar, User, ChevronDown, LogOut, Clock, ArrowRight, X, ChevronsLeft, ChevronsRight, Menu, UserCircle } from 'lucide-react'

interface NewsArticle {
  id: string
  title: string
  excerpt: string | null
  content: string
  image_url: string | null
  category: string
  author_name: string | null
  published_at: string | null
  created_at: string
}

export default function NewsPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user, logout } = useAuthStore()
  const [newsArticles, setNewsArticles] = useState<NewsArticle[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [selectedArticle, setSelectedArticle] = useState<NewsArticle | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const categories = ['All', 'General', 'Awards', 'Events', 'Partnership']
  const articlesPerPage = 8

  const getDashboardRoute = () => {
    if (user?.role === 'admin') return '/admin'
    if (user?.role === 'alumni') return '/dashboard'
    if (user?.role === 'employer') return '/employer-dashboard'
    return '/dashboard'
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleLogout = () => {
    setIsDropdownOpen(false)
    logout()
  }

  useEffect(() => {
    fetchNews()
  }, [])

  // Check for article ID in URL and open it
  useEffect(() => {
    const articleId = searchParams.get('articleId')
    if (articleId && newsArticles.length > 0) {
      const article = newsArticles.find(a => a.id === articleId)
      if (article) {
        setSelectedArticle(article)
        // Remove the articleId from URL to clean it up
        searchParams.delete('articleId')
        setSearchParams(searchParams, { replace: true })
      }
    }
  }, [newsArticles, searchParams, setSearchParams])

  const fetchNews = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabaseClient
        .from('news')
        .select('*')
        .eq('status', 'published')
        .order('published_at', { ascending: false })

      if (error) throw error
      setNewsArticles(data || [])
    } catch (err) {
      console.error('Error fetching news:', err)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const getTimeAgo = (dateString: string | null): string => {
    if (!dateString) return ''
    const date = new Date(dateString)
    const now = new Date()
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000)

    if (seconds < 60) return 'Just now'
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes} min ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours} hr ago`
    const days = Math.floor(hours / 24)
    if (days < 30) return `${days} day${days > 1 ? 's' : ''} ago`
    const months = Math.floor(days / 30)
    if (months < 12) return `${months} month${months > 1 ? 's' : ''} ago`
    const years = Math.floor(months / 12)
    return `${years} year${years > 1 ? 's' : ''} ago`
  }

  const stripHtml = (html: string | null): string => {
    if (!html) return ''
    return html.replace(/<[^>]*>/g, '').trim()
  }

  const filteredNews = selectedCategory === 'all'
    ? newsArticles
    : newsArticles.filter(article => article.category.toLowerCase() === selectedCategory.toLowerCase())

  const featuredArticle = filteredNews[0]
  const otherArticles = filteredNews.slice(1)

  // Pagination calculations
  const totalPages = Math.ceil(otherArticles.length / articlesPerPage)
  const startIndex = (currentPage - 1) * articlesPerPage
  const endIndex = startIndex + articlesPerPage
  const currentArticles = otherArticles.slice(startIndex, endIndex)

  // Reset to page 1 when category changes
  useEffect(() => {
    setCurrentPage(1)
  }, [selectedCategory])

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handleFirstPage = () => {
    setCurrentPage(1)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleLastPage = () => {
    setCurrentPage(totalPages)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleGoToPage = (page: number) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = []
    const maxVisible = 5 // Maximum page numbers to show

    if (totalPages <= maxVisible) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      // Always show first page
      pages.push(1)

      if (currentPage > 3) {
        pages.push('...')
      }

      // Show pages around current page
      const start = Math.max(2, currentPage - 1)
      const end = Math.min(totalPages - 1, currentPage + 1)

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }

      if (currentPage < totalPages - 2) {
        pages.push('...')
      }

      // Always show last page
      pages.push(totalPages)
    }

    return pages
  }

  const getCategoryColor = (category: string): string => {
    switch (category) {
      case 'Awards':
        return 'bg-purple-100 text-purple-800'
      case 'Events':
        return 'bg-blue-100 text-blue-800'
      case 'Partnership':
        return 'bg-indigo-100 text-indigo-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading news articles...</p>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-2">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img 
                src="/trailblaze_logo.png" 
                alt="Trailblaze Graduate Tracer System" 
                className="h-20"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-8">
              <Link to="/news" className="text-blue-600 font-medium">News & Updates</Link>
              <Link to="/#features" className="text-gray-600 hover:text-gray-900">Features</Link>
              <Link to="/#benefits" className="text-gray-600 hover:text-gray-900">Benefits</Link>
              <Link to="/#about" className="text-gray-600 hover:text-gray-900">About UCLM</Link>
            </nav>

            {/* Desktop - Conditional Right Side */}
            <div className="hidden md:flex">
            {user ? (
              <div className="flex items-center space-x-4">
                {/* Dashboard Button */}
                <button
                  onClick={() => navigate(getDashboardRoute())}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors text-sm"
                >
                  Dashboard
                </button>

                {/* Profile Dropdown */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="flex items-center space-x-2 focus:outline-none"
                  >
                    <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center hover:bg-gray-400 transition-colors overflow-hidden">
                      {user?.profile && 'profile_picture' in user.profile && user.profile.profile_picture ? (
                        <img
                          src={user.profile.profile_picture}
                          alt="Profile"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = '/default-avatar.svg'
                          }}
                        />
                      ) : (
                        <User className="w-6 h-6 text-gray-600" />
                      )}
                    </div>
                    <ChevronDown className={`w-4 h-4 text-gray-600 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Dropdown Menu */}
                  {isDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                      <div className="px-4 py-2 border-b border-gray-200">
                        <p className="text-sm font-medium text-gray-900 truncate">{user?.email}</p>
                        <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
                      </div>
                      <button
                        onClick={() => {
                          setIsDropdownOpen(false)
                          if (user?.role === 'admin') {
                            navigate('/admin/profile')
                          } else if (user?.role === 'alumni') {
                            navigate('/profile')
                          } else if (user?.role === 'employer') {
                            navigate('/employer/profile')
                          }
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                      >
                        <UserCircle className="w-4 h-4" />
                        <span>View Profile</span>
                      </button>
                      <button
                        onClick={handleLogout}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Logout</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* Login Button */
              <Link 
                to="/login"
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
              >
                Login
              </Link>
            )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-gray-600" />
              ) : (
                <Menu className="w-6 h-6 text-gray-600" />
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              <nav className="flex flex-col space-y-4">
                <Link
                  to="/news"
                  className="text-blue-600 font-medium px-2 py-2 hover:bg-gray-50 rounded"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  News & Updates
                </Link>
                <Link
                  to="/#features"
                  className="text-gray-600 hover:text-gray-900 px-2 py-2 hover:bg-gray-50 rounded"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Features
                </Link>
                <Link
                  to="/#benefits"
                  className="text-gray-600 hover:text-gray-900 px-2 py-2 hover:bg-gray-50 rounded"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Benefits
                </Link>
                <Link
                  to="/#about"
                  className="text-gray-600 hover:text-gray-900 px-2 py-2 hover:bg-gray-50 rounded"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  About UCLM
                </Link>
                
                {/* Mobile Auth Section */}
                <div className="pt-4 border-t border-gray-200">
                  {user ? (
                    <div className="space-y-3">
                      <button
                        onClick={() => {
                          navigate(getDashboardRoute())
                          setIsMobileMenuOpen(false)
                        }}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors text-sm"
                      >
                        Dashboard
                      </button>
                      <div className="flex items-center justify-between px-2 py-2 bg-gray-50 rounded">
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden">
                            {user?.profile && 'profile_picture' in user.profile && user.profile.profile_picture ? (
                              <img
                                src={user.profile.profile_picture}
                                alt="Profile"
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = '/default-avatar.svg'
                                }}
                              />
                            ) : (
                              <User className="w-4 h-4 text-gray-600" />
                            )}
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-900 truncate">{user?.email}</p>
                            <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            handleLogout()
                            setIsMobileMenuOpen(false)
                          }}
                          className="text-gray-600 hover:text-red-600 p-2"
                        >
                          <LogOut className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <Link
                      to="/login"
                      className="block w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors text-center"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Login
                    </Link>
                  )}
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Main Content with Gradient Background */}
      <div className="bg-gray-50 min-h-screen py-8 overflow-x-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-blue-50 to-white rounded-lg p-4 sm:p-8">
            {/* Welcome Header */}
            <div className="text-center mb-6 sm:mb-8">
              <p className="text-xs sm:text-sm text-gray-600 uppercase tracking-widest mb-2 sm:mb-3 font-medium">Welcome to UCLM News</p>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-gray-900 leading-tight px-2">
                Stay Informed with the Latest University News, Events, and Achievements
              </h1>
            </div>

            {/* Category Filter */}
            <div className="mb-8 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
              <div className="flex justify-center">
                <div className="inline-flex items-center gap-2 bg-white rounded-lg p-1 shadow-sm min-w-min">
                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category.toLowerCase())}
                      className={`px-3 sm:px-4 py-2 rounded-md font-medium transition whitespace-nowrap text-sm sm:text-base flex-shrink-0 ${
                        selectedCategory === category.toLowerCase()
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {filteredNews.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <p className="text-gray-600 text-lg">
                  {selectedCategory === 'all'
                    ? 'No news articles available at the moment.'
                    : `No ${selectedCategory} articles found.`}
                </p>
              </div>
            ) : (
              <>
                {/* Featured Article */}
                {featuredArticle && (
                  <div className="mb-12">
                    <div 
                      onClick={() => setSelectedArticle(featuredArticle)}
                      className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow cursor-pointer"
                    >
                      <div className="grid md:grid-cols-2 gap-0">
                        {/* Image */}
                        <div className="relative h-64 md:h-full min-h-[400px]">
                          {featuredArticle.image_url ? (
                            <img
                              src={featuredArticle.image_url}
                              alt={featuredArticle.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                              <Calendar className="h-24 w-24 text-white opacity-50" />
                            </div>
                          )}
                        </div>

                        {/* Content */}
                        <div className="p-8 flex flex-col justify-center">
                          {/* Meta Info */}
                          <div className="flex items-center gap-3 mb-4">
                            {featuredArticle.author_name && (
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <User className="h-4 w-4" />
                                <span className="font-medium">
                                  {featuredArticle.author_name.split('@')[0]}
                                </span>
                              </div>
                            )}
                            <span className="text-gray-400">•</span>
                            <span className="text-sm text-gray-500">
                              {getTimeAgo(featuredArticle.published_at || featuredArticle.created_at)}
                            </span>
                          </div>

                          {/* Title */}
                          <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-4 leading-tight">
                            {featuredArticle.title}
                          </h2>

                          {/* Excerpt */}
                          {featuredArticle.excerpt && stripHtml(featuredArticle.excerpt) && (
                            <p className="text-gray-600 text-base mb-4 leading-relaxed italic border-l-4 border-blue-500 pl-4">
                              {stripHtml(featuredArticle.excerpt)}
                            </p>
                          )}

                          {/* Content Preview */}
                          <p className="text-gray-700 text-base mb-6 leading-relaxed line-clamp-6">
                            {featuredArticle.content.replace(/<[^>]*>/g, '').substring(0, 400)}...
                          </p>

                          {/* Tags */}
                          <div className="flex items-center gap-4 text-sm">
                            <span className={`px-3 py-1 rounded-full font-medium ${getCategoryColor(featuredArticle.category)}`}>
                              {featuredArticle.category}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Latest News Section */}
                {otherArticles.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-2xl font-serif font-bold text-gray-900">Latest News</h2>
                      <div className="text-sm text-gray-600">
                        Page {currentPage} of {totalPages}
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                      {currentArticles.map((article) => (
                        <article
                          key={article.id}
                          onClick={() => setSelectedArticle(article)}
                          className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition group cursor-pointer"
                        >
                          {/* Image */}
                          <div className="relative h-48 overflow-hidden">
                            {article.image_url ? (
                              <img
                                src={article.image_url}
                                alt={article.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                            ) : (
                              <div className="h-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                                <Calendar className="h-16 w-16 text-white opacity-50" />
                              </div>
                            )}
                            {/* Category Badge - Overlayed on image */}
                            <div className="absolute top-3 left-3">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium shadow-sm ${getCategoryColor(article.category)}`}>
                                {article.category}
                              </span>
                            </div>
                          </div>

                          {/* Content */}
                          <div className="p-5">
                            {/* Meta Info */}
                            <div className="flex items-center gap-2 mb-3 text-xs text-gray-500">
                              {article.author_name && (
                                <>
                                  <span className="font-medium">
                                    {article.author_name.split('@')[0]}
                                  </span>
                                  <span>•</span>
</>
                              )}
                              <span>
                                {getTimeAgo(article.published_at || article.created_at)}
                              </span>
                            </div>

                            {/* Title */}
                            <h3 className="text-base font-serif font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-blue-600 transition">
                              {article.title}
                            </h3>

                            {/* Excerpt */}
                            {article.excerpt && stripHtml(article.excerpt) && (
                              <p className="text-xs text-gray-600 mb-2 italic line-clamp-2">
                                {stripHtml(article.excerpt)}
                              </p>
                            )}

                            {/* Content Preview */}
                            <p className="text-sm text-gray-600 mb-3 line-clamp-3">
                              {article.content.replace(/<[^>]*>/g, '').substring(0, 150) + '...'}
                            </p>
                          </div>
                        </article>
                      ))}
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-center gap-2 mt-8 flex-wrap">
                        {/* First Page Button */}
                        <button
                          onClick={handleFirstPage}
                          disabled={currentPage === 1}
                          className={`p-2 rounded-lg font-medium transition ${
                            currentPage === 1
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-600 shadow-sm'
                          }`}
                          title="First page"
                        >
                          <ChevronsLeft className="h-5 w-5" />
                        </button>

                        {/* Previous Button */}
                        <button
                          onClick={handlePreviousPage}
                          disabled={currentPage === 1}
                          className={`p-2 rounded-lg font-medium transition ${
                            currentPage === 1
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-600 shadow-sm'
                          }`}
                          title="Previous page"
                        >
                          <ArrowRight className="h-5 w-5 rotate-180" />
                        </button>

                        {/* Page Numbers */}
                        <div className="flex items-center gap-2">
                          {getPageNumbers().map((page, index) => (
                            page === '...' ? (
                              <span key={`ellipsis-${index}`} className="px-2 text-gray-400">
                                ...
                              </span>
                            ) : (
                              <button
                                key={page}
                                onClick={() => handleGoToPage(page as number)}
                                className={`w-10 h-10 rounded-lg font-medium transition ${
                                  currentPage === page
                                    ? 'bg-blue-600 text-white shadow-sm'
                                    : 'bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-600 shadow-sm'
                                }`}
                              >
                                {page}
                              </button>
                            )
                          ))}
                        </div>

                        {/* Next Button */}
                        <button
                          onClick={handleNextPage}
                          disabled={currentPage === totalPages}
                          className={`p-2 rounded-lg font-medium transition ${
                            currentPage === totalPages
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-600 shadow-sm'
                          }`}
                          title="Next page"
                        >
                          <ArrowRight className="h-5 w-5" />
                        </button>

                        {/* Last Page Button */}
                        <button
                          onClick={handleLastPage}
                          disabled={currentPage === totalPages}
                          className={`p-2 rounded-lg font-medium transition ${
                            currentPage === totalPages
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-600 shadow-sm'
                          }`}
                          title="Last page"
                        >
                          <ChevronsRight className="h-5 w-5" />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Article Detail Modal */}
      {selectedArticle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto overflow-x-hidden my-4 sm:my-8">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between z-10">
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(selectedArticle.category)}`}>
                  {selectedArticle.category}
                </span>
              </div>
              <button
                onClick={() => setSelectedArticle(null)}
                className="p-2 hover:bg-gray-100 rounded-full transition flex-shrink-0"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 sm:p-8 overflow-x-hidden">
              {/* Featured Image */}
              {selectedArticle.image_url && (
                <div className="relative h-48 sm:h-64 md:h-96 rounded-lg overflow-hidden mb-4 sm:mb-8">
                  <img
                    src={selectedArticle.image_url}
                    alt={selectedArticle.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              {/* Article Meta */}
              <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6 flex-wrap">
                {selectedArticle.author_name && (
                  <div className="flex items-center gap-1 sm:gap-2">
                    <User className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                    <span className="font-medium truncate max-w-[100px] sm:max-w-none">
                      {selectedArticle.author_name.split('@')[0]}
                    </span>
                  </div>
                )}
                <span className="text-gray-400">•</span>
                <div className="flex items-center gap-1 sm:gap-2">
                  <Calendar className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                  <span className="text-xs sm:text-sm">{formatDate(selectedArticle.published_at || selectedArticle.created_at)}</span>
                </div>
                <span className="text-gray-400 hidden sm:inline">•</span>
                <div className="hidden sm:flex items-center gap-1 sm:gap-2">
                  <Clock className="h-4 w-4 flex-shrink-0" />
                  <span>{getTimeAgo(selectedArticle.published_at || selectedArticle.created_at)}</span>
                </div>
              </div>

              {/* Article Title */}
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-4 sm:mb-6 leading-tight break-words">
                {selectedArticle.title}
              </h1>

              {/* Article Excerpt */}
              {selectedArticle.excerpt && stripHtml(selectedArticle.excerpt) && (
                <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-6 sm:mb-8 leading-relaxed border-l-4 border-blue-500 pl-3 sm:pl-4 italic break-words">
                  {stripHtml(selectedArticle.excerpt)}
                </p>
              )}

              {/* Article Content */}
              <div className="prose prose-sm sm:prose-base md:prose-lg max-w-none overflow-x-hidden">
                <div 
                  className="text-gray-700 leading-relaxed break-words [&>*]:max-w-full [&_img]:max-w-full [&_img]:h-auto [&_table]:max-w-full [&_table]:overflow-x-auto [&_table]:block [&_pre]:max-w-full [&_pre]:overflow-x-auto"
                  dangerouslySetInnerHTML={{ __html: selectedArticle.content }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
